import React from "react";

import { ProductTileUpperContainer } from "../../ComposableProductTile/__internal__/__internal__/components/ProductTileUpperContainer/ProductTileUpperContainer";
import { ILoggingContext, ITileInteractions, ITrackingProps } from "../types";

import { ProductTileClientContainer } from "./components/ProductTileClientContainer";
import { ProductTileImpressionTrackingWrapper } from "./components/ProductTileImpressionTrackingWrapper";
import { SwatchInteractionProvider } from "./components/Swatch/SwatchInteractionContextProvider";
import { TileInteractionsContextProvider } from "./components/TileInteractionsContextProvider";

/**
 * Props for the ProductTileTemplate component
 */
export interface ProductTileTemplateProps {
  /** Required logging context for tracking impressions */
  loggingContext: ILoggingContext;
  /** Required tracking properties for analytics */
  trackingProps: ITrackingProps;
  /** Optional tile interactions for handling user interactions */
  tileInteractions?: ITileInteractions;
  /** Required top slot, typically the product image */
  topSlot: React.ReactNode;
  /** Optional overlay slot for additional UI elements */
  overlaySlot?: React.ReactNode;
  /** Optional middle slot for content between top and bottom slot */
  middleSlot?: React.ReactNode;
  /** Optional bottom slot for content below the image */
  bottomSlot?: React.ReactNode;
}

/**
 * A template component for rendering product tiles with consistent structure and behavior.
 * Handles impression tracking, click tracking, and user interactions.
 */
export const ProductTileTemplate = ({
  loggingContext,
  trackingProps,
  tileInteractions,
  topSlot,
  overlaySlot,
  middleSlot,
  bottomSlot,
}: ProductTileTemplateProps) => {
  return (
    <ProductTileImpressionTrackingWrapper loggingContext={loggingContext}>
      {/* TODO(bronwyn): Add swatch interactions to tile context provider once all tiles are migrated */}
      <SwatchInteractionProvider>
        <TileInteractionsContextProvider
          data={tileInteractions}
          trackingProps={trackingProps}
        >
          <ProductTileClientContainer data={trackingProps}>
            <div className="relative flex flex-col" data-test-id="product-tile">
              <ProductTileUpperContainer>
                {topSlot}
                {overlaySlot}
              </ProductTileUpperContainer>
              {middleSlot}
              {bottomSlot}
            </div>
          </ProductTileClientContainer>
        </TileInteractionsContextProvider>
      </SwatchInteractionProvider>
    </ProductTileImpressionTrackingWrapper>
  );
};
